// (c) chaehongee. Confidential and proprietary.

/**
 * Names of files the browser labels as images but cannot actually decode.
 *
 * `file.type.startsWith('image/')` is not a decode check — a HEIC from an
 * iPhone reports `image/heic` and sails through it, then the Replicate
 * trainer dies an hour later with "cannot identify image file". Decoding
 * each file here turns that into an instant, fixable error.
 */
export async function findUndecodableImages(files: File[]): Promise<string[]> {
    const results = await Promise.all(
        files.map(async (file) => ((await canDecode(file)) ? null : file.name))
    )
    return results.filter((name): name is string => name !== null)
}

async function canDecode(file: File): Promise<boolean> {
    // createImageBitmap does a real decode and rejects on formats the browser
    // cannot read, which is exactly the set the trainer cannot read either.
    try {
        const bitmap = await createImageBitmap(file)
        bitmap.close()
        return true
    } catch {
        return false
    }
}

export async function prepareTrainingData(files: FileList): Promise<Blob | File> {
    if (!files || files.length === 0) {
        throw new Error('No training files provided.')
    }

    const firstFile = files.item(0)
    const looksLikeZip =
        files.length === 1 &&
        !!firstFile &&
        (firstFile.type === 'application/zip' ||
            firstFile.name.toLowerCase().endsWith('.zip'))

    return looksLikeZip ? firstFile! : await createTrainingZip(files)
}

async function createTrainingZip(files: FileList): Promise<Blob> {
    const JSZip = await loadJSZip()
    const zip = new JSZip()
    Array.from(files).forEach((file) => zip.file(file.name, file))
    return zip.generateAsync({ type: 'blob' })
}

async function loadJSZip() {
    const mod = await import('jszip')
    return mod.default
}
